/**
 * Case-scoped Excise-Tax (Form 4180) workflow
 * (`/client/:id/cases/:caseId/workflow/excise[/:step]`, milestone 03-17).
 *
 * Resolves the client's one Form 4180 submission, links it to the case, and
 * starts/resumes a `ResolutionWorkflow` (type EXCISE). The `:step` URL param owns
 * the active section so deep-linking + refresh resume where the practitioner left
 * off; step changes persist as the workflow's `current_step`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { FiArrowLeft } from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader } from "@/components/common/loader";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { getOrCreateSubmission, getSubmission, linkSubmissionCase } from "@/features/forms/api";
import type { FormSubmission } from "@/features/forms/types";
import {
  patchWorkflow,
  resolveTaxFormId,
  startOrResumeWorkflow,
} from "@/features/workflows/api";
import { ExciseWizard } from "@/features/workflows/excise/excise-wizard";
import { EXCISE_STEPS, type ResolutionWorkflow, type WorkflowStep } from "@/features/workflows/types";
import { useActiveClient } from "@/layouts/client-layout";
import { ApiError } from "@/lib/api";

const STEP_KEYS = EXCISE_STEPS.map((s) => s.key);
const isStep = (v: string | undefined): v is WorkflowStep =>
  !!v && (STEP_KEYS as string[]).includes(v);

export default function ExciseWorkflowPage() {
  const { client } = useActiveClient();
  const { caseId = "", step } = useParams<{ caseId: string; step: string }>();
  const navigate = useNavigate();

  const [submission, setSubmission] = useState<FormSubmission | null>(null);
  const [workflow, setWorkflow] = useState<ResolutionWorkflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const base = `/client/${client.id}/cases/${caseId}/workflow/excise`;
  const active: WorkflowStep = isStep(step) ? step : "section1";

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    (async () => {
      const taxFormId = await resolveTaxFormId("4180");
      let sub = await getOrCreateSubmission(client.id, taxFormId);
      if (sub.case !== caseId) sub = await linkSubmissionCase(sub.id, caseId);
      sub = await getSubmission(sub.id);
      const wf = await startOrResumeWorkflow(caseId, "EXCISE");
      return { sub, wf };
    })()
      .then(({ sub, wf }) => {
        if (!mountedRef.current) return;
        setSubmission(sub);
        setWorkflow(wf);
      })
      .catch((err) => {
        if (mountedRef.current) {
          setError(err instanceof ApiError ? err.message : "Couldn't open this workflow.");
        }
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [client.id, caseId]);

  // Resume: landing on the base (no/invalid step) routes to the saved step.
  useEffect(() => {
    if (!isStep(step) && workflow) {
      navigate(`${base}/${workflow.currentStep}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, workflow]);

  // Persist the active step as the resume target whenever it changes.
  useEffect(() => {
    if (!workflow || !isStep(step) || workflow.currentStep === active) return;
    patchWorkflow(caseId, workflow.id, { currentStep: active })
      .then((updated) => {
        if (mountedRef.current) setWorkflow(updated);
      })
      .catch(() => {
        /* progress is a resume convenience — a failed persist won't block nav */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, workflow]);

  const goto = useCallback((s: WorkflowStep) => navigate(`${base}/${s}`), [base, navigate]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to={`/client/${client.id}/cases/${caseId}`}>
            <FiArrowLeft className="size-4" /> Back to case
          </Link>
        </Button>
        <h2 className="text-foreground text-2xl font-bold">Excise Tax Workflow (Form 4180)</h2>
      </div>

      {loading ? (
        <Loader className="mt-16" />
      ) : error || !submission || !workflow ? (
        <Alert variant="destructive" className="mt-8">
          <AlertTitle>Couldn't open this workflow</AlertTitle>
          <AlertDescription>{error ?? "Please try again."}</AlertDescription>
        </Alert>
      ) : (
        <ExciseWizard
          client={client}
          caseId={caseId}
          submission={submission}
          onSubmissionChange={setSubmission}
          workflow={workflow}
          onWorkflowChange={setWorkflow}
          activeStep={active}
          onSelectStep={goto}
        />
      )}
    </div>
  );
}
